import { useCallback, useState, useRef } from 'react'
import moment from 'moment'

import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import app from 'modules/core/store/models/app'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'
import { handleResult } from 'modules/core/lib/handleMutation'

import { useCreatePost } from 'modules/posts/hooks/useCreatePost'

interface CommunityQuickPostEditorProps {
  groupId: number
}

function CommunityQuickPostEditor({ groupId }: CommunityQuickPostEditorProps) {
  const { editorConfig, loading: loadingEditorConfig } = useRichTextEditor()
  const [editorContent, setEditorContent] = useState('')
  const { createPost, loadingCreatePost } = useCreatePost()
  const editorRef = useRef<RichTextEditorHandle>(null)

  function getTitleFromContent(content: string): string {
    if (!content.trim()) return i18n.t('posts.communities.quick_post.default_title')

    const plainText = content.replace(/<[^>]*>/g, '').trim()
    const maxLength = 60
    if (plainText.length <= maxLength) return plainText

    const truncated = plainText.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    const contentFirstWords = lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated
    return `${contentFirstWords}...`
  }

  const savePost = useCallback(async () => {
    const result = await createPost({
      description: editorContent,
      mentionedAccessIds: [],
      postGroupId: groupId,
      publishedAt: moment().toISOString(),
      title: getTitleFromContent(editorContent),
      type: "announcement",
      allowCommentsAndReactions: true,
      sendNotifications: false,
    })
    const handledResult = handleResult(result, () => { })

    if (handledResult.success) {
      clearEditor()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.quick_post.error_message'),
      })
    }
  }, [editorContent, createPost, groupId])

  function clearEditor() {
    setEditorContent('')
    editorRef.current?.setContent('')
  }

  return loadingEditorConfig ? (
    <RichTextEditor.Skeleton rows={1} />
  ) : (
    <RichTextEditor
      ref={editorRef}
      {...editorConfig}
      title={getTitleFromContent(editorContent)}
      placeholder={i18n.t('posts.communities.quick_post.placeholder')}
      maxCharacters={10000}
      initialEditorState={{ content: editorContent }}
      onChange={(result) => setEditorContent(result?.value ?? '')}
      primaryAction={{
        action: {
          label: loadingCreatePost
            ? i18n.t('common.saving')
            : i18n.t('posts.communities.quick_post.button'),
          onClick: savePost,
          variant: 'default',
          disabled: loadingCreatePost || !editorContent.trim(),
        },
      }}
    />
  )
}

export default CommunityQuickPostEditor